import { formatThousandSeparator } from "@/utils/money";
import { cloneDeep } from "lodash-es";

// 深度合并对象, 相同字段，保留参数1
const deepAssign = (...sources: any[]) => {
    const result: { [key: string]: any } = {};
    const stack = [{ target: result, sources }];
    const map = new WeakMap();

    while (stack.length) {
        const item = stack.pop();
        if (!item) return;
        const { target, sources } = item;

        for (const source of sources) {
            if (source && typeof source === "object") {
                if (map.has(source)) {
                    const _key = map.get(source);
                    if (typeof _key == "object") {
                        continue;
                    }
                    target[_key] = source;
                    continue; // 处理循环引用
                }
                map.set(source, target);

                for (const [key, value] of Object.entries(source)) {
                    if (value && typeof value === "object") {
                        if (map.has(value)) {
                            target[key] = map.get(value);
                        } else {
                            if (
                                !target[key] ||
                                typeof target[key] !== "object" ||
                                Array.isArray(target[key]) !== Array.isArray(value)
                            ) {
                                target[key] = Array.isArray(value) ? [] : {};
                            }
                            stack.push({
                                target: target[key],
                                sources: [target[key], value]
                            });
                        }
                    } else {
                        target[key] = value;
                    }
                }
            }
        }
    }

    return result;
};

// 饼图默认数据
export const getPicOption = (option: { series: any; [key: string]: any }) => {
    option = cloneDeep(option);
    const data = {
        legend: {
            itemGap: 20, // 设置图例项之间的间隔为20像素
            align: "left",
            orient: "vertical", // 垂直排列
            left: "25%",
            top: "center",
            icon: "circle",
            itemWidth: 6,
            itemHeight: 6,
            textStyle: {
                rich: {
                    a: {
                        color: "#333333",
                        fontSize: 16,
                        fontWeight: 400,
                        lineHeight: 18
                    },
                    b: {
                        color: "#333333",
                        fontSize: 32,
                        fontWeight: 500,
                        lineHeight: 32
                    },
                    c: {
                        color: "#999999",
                        fontSize: 14,
                        fontWeight: 400
                    }
                }
            },
            formatter(name: string) {
                const data = option.series[0].data;
                const item = data.find((x: { name: string }) => x.name === name) as { value: number | string };
                if (!item) return name;
                return "{a|" + name + "}" + "{b| " + item.value + "}" + "{c| 个}";
            }
        },
        series: [
            {
                type: "pie",
                radius: ["30%", "92%"],
                center: ["10%", "50%"],
                padAngle: 2,
                label: {
                    position: "inside",
                    show: true,
                    formatter: "{d}%",
                    color: "#fff",
                    fontSize: 10
                },
                data: [
                    { value: 0, name: "累计联合采购项目量" },
                    { value: 0, name: "累计直采项目量" }
                ],
                itemStyle: {
                    color(params: { dataIndex: number }) {
                        const colorList = ["#1886FE", "#14C9C9"];
                        return colorList[params.dataIndex % colorList.length];
                    }
                }
            }
        ]
    };
    return deepAssign(option, data);
};
// 圆环图默认数据
export const getAnnularOption = (option: { series: any; [key: string]: any }) => {
    option = cloneDeep(option);
    const data = {
        legend: {
            itemGap: 20, // 设置图例项之间的间隔为20像素
            align: "left",
            orient: "vertical", // 垂直排列
            left: "60%",
            top: "middle",
            icon: "circle",
            itemWidth: 8,
            itemHeight: 8,
            textStyle: {
                color: "#000000b3",
                fontSize: 14
            },
            formatter(name: string) {
                const data = option.series[0].data;
                const item = data.find((x: { name: string }) => x.name === name) as { value: number | string };
                if (!item) return name;
                let total = 0;
                data.forEach((x: { value: number }) => (total += Number(x.value)));
                return `${name} ${((Number(item.value) / total) * 100).toFixed(2)}%`;
            }
        },
        series: [
            {
                type: "pie",
                radius: ["60%", "80%"],
                center: ["30%", "50%"],
                label: {
                    position: "center",
                    show: false
                },
                emphasis: {
                    label: {
                        show: true,
                        formatter(item: { name: string; value: number }) {
                            return [
                                "{a|" + item.name + "}",
                                "{b|" + formatThousandSeparator(item.value) + "}" + "{c| 万元}"
                            ].join("\n");
                        },
                        rich: {
                            a: {
                                color: "#000000e6",
                                fontSize: 14,
                                lineHeight: 40
                            },
                            b: {
                                color: "#000000e6",
                                fontSize: 32,
                                verticalAlign: "bottom"
                            },
                            c: {
                                color: "#999999",
                                fontSize: 12,
                                verticalAlign: "bottom"
                            }
                        }
                    }
                },
                data: [
                    { value: 0, name: "text1" },
                    { value: 0, name: "text2" }
                ],
                itemStyle: {
                    color(params: { dataIndex: number }) {
                        const colorList = ["#1886FE", "#14C9C9"];
                        return colorList[params.dataIndex % colorList.length];
                    }
                }
            }
        ]
    };
    return deepAssign(option, data);
};
// 默认柱状图
export const getBarOption = (option: { series: any; [key: string]: any }) => {
    const data = {
        grid: {
            left: "1%",
            right: "4%",
            bottom: "10%",
            containLabel: true
        },
        title: {
            text: "金额 (万元)",
            textStyle: {
                color: "#000000b3",
                fontSize: 12
            }
        },
        tooltip: {
            trigger: "axis",
            confine: true,
            axisPointer: {
                type: "shadow"
            }
        },
        legend: {
            itemGap: 40, // 设置图例项之间的间隔为20像素
            icon: "rect",
            bottom: 0,
            itemWidth: 8,
            itemHeight: 8
        },
        yAxis: [
            {
                type: "value"
            }
        ],
        series: [
            {
                name: "Forest",
                type: "bar",
                barGap: 0,
                emphasis: {
                    focus: "series"
                },
                data: [320, 332, 301, 334, 390]
            }
        ]
    };
    return deepAssign(option, data);
};

// 折线图默认数据
export const getLineOption = (option: { series: any; [key: string]: any }) => {
    option = cloneDeep(option);
    const data = {
        title: {
            text: "金额(万元)",
            textStyle: {
                color: "#000000b3",
                fontSize: 12
            }
        },

        tooltip: {
            trigger: "axis",
            confine: true,
        },
        legend: {
            itemGap: 40, // 设置图例项之间的间隔为20像素
            icon: "rect",
            bottom: "0%",
            itemWidth: 16,
            itemHeight: 2
        },
        grid: {
            left: "1%",
            right: "4%",
            bottom: "10%",
            containLabel: true
        },
        xAxis: {
            type: "category",
            data: [],
            axisTick: {
                show: false // 设置x轴刻度显示
            },
            axisLine: {
                lineStyle: {
                    color: "#d9d9d9"
                }
            },
            axisLabel: {
                color: "#000000"
            }
        },
        yAxis: {
            type: "value",
            min(value: { min: number }) {
                if (value.min === 2 || value.min - 2 < 0) {
                    return 0;
                } else {
                    return (value.min - 2).toFixed();
                }
            },
            // min: "dataMin",
            // max: "dataMax",
            splitLine: {
                lineStyle: {
                    type: "dashed" // 设置虚线类型
                }
            }
        },
        series: [
            // {
            //     name: "预付款充值",
            //     type: "line",
            //     data: [],
            //     lineStyle: {
            //         color: "#1777FF"
            //     }
            // },
            // {
            //     name: "账单服务费",
            //     type: "line",
            //     data: [],
            //     lineStyle: {
            //         color: "#6FD195"
            //     }
            // },
            // {
            //     name: "已核销服务费",
            //     type: "line",
            //     data: [],
            //     lineStyle: {
            //         color: "#FFAE4C"
            //     }
            // }
        ]
    };
    return deepAssign(option, data);
};

// 层叠柱状图
export const getTtackBarOption = ({
    titleText,
    series,
    xAxisData,
    barBorderRadius,
    grid
}: {
    titleText: string;
    series: any[];
    xAxisData: any[];
    barBorderRadius: number;
    grid?: any;
}) => {
    series = cloneDeep(series);
    xAxisData = cloneDeep(xAxisData);
    const stackInfo: { [key: string]: any } = {};
    for (let i = 0; i < series[0].data.length; ++i) {
        for (let j = 0; j < series.length; ++j) {
            const stackName = series[j].stack;
            if (!stackName) {
                continue;
            }
            if (!stackInfo[stackName]) {
                stackInfo[stackName] = {
                    stackStart: [],
                    stackEnd: []
                };
            }
            const info = stackInfo[stackName];
            const data = series[j].data[i];
            if (data && data !== "-") {
                if (info.stackStart[i] == null) {
                    info.stackStart[i] = j;
                }
                info.stackEnd[i] = j;
            }
        }
    }
    for (let i = 0; i < series.length; ++i) {
        const data = series[i].data;
        const info = stackInfo[series[i].stack];
        for (let j = 0; j < series[i].data.length; ++j) {
            const isEnd = info.stackEnd[j] === i;
            const topBorder = isEnd ? barBorderRadius : 0;
            const bottomBorder = i === 0 ? barBorderRadius : 0;
            data[j] = {
                value: data[j],
                itemStyle: {
                    borderRadius: [topBorder, topBorder, bottomBorder, bottomBorder]
                }
            };
        }
    }
    return {
        title: {
            text: titleText,
            textStyle: {
                color: "#000000b3",
                fontSize: 12
            }
        },
        tooltip: {
            trigger: "axis",
            confine: true
        },
        legend: {
            itemGap: 40, // 设置图例项之间的间隔为20像素
            icon: "rect",
            bottom: 0,
            itemWidth: 8,
            itemHeight: 8
        },
        grid: grid
            ? grid
            : {
                  left: "1%",
                  right: "4%",
                  bottom: "15%",
                  containLabel: true
              },
        xAxis: {
            type: "category",
            data: xAxisData,
            axisTick: {
                show: false // 设置x轴刻度显示
            },
            axisLine: {
                lineStyle: {
                    color: "#d9d9d9"
                }
            },
            axisLabel: {
                color: "#000000"
            }
        },
        yAxis: {
            type: "value",
            min: 0,
            // max: "dataMax",
            splitLine: {
                lineStyle: {
                    type: "dashed" // 设置虚线类型
                }
            }
        },
        series: series
    };
};
